import { fetchChannels } from 'api/Channels/actions'
import { fetchCountries } from 'api/Countries/actions'
import { ChannelSummary } from 'ChannelSummary'
import type { DetailsType } from 'ChannelSummary/constants'
import classNames from 'classnames'
import { CountriesList } from 'CountriesList'
import {
  selectChannelsList,
  selectChannelsType,
  selectRefetchParams,
  selectSubListTypeSelected,
  selectTabRequestParams,
} from 'data/selectors'
import { GroupDetails } from 'GroupDetails'
import { ListDetails } from 'ListDetails'
import { selectIsListDetailsOpen } from 'ListDetails/selectors'
import { LoadingOverlay } from 'LoadingOverlay'
import React, { memo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SaleTypesList } from 'SaleTypesList'
import { selectLastReFetchTime } from 'store/reducers/lastReFetchTime'

import Box from '@mui/material/Box'

import useStyles from './useStyles'

export interface ChannelsListProps {
  countryId?: string
  detailsType?: DetailsType
  saleType?: string
}

function ChannelsList({ countryId, detailsType, saleType }: ChannelsListProps) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { isLoading, list } = useSelector(selectChannelsList({ countryId, saleType }))
  const subListTypeSelected = useSelector(selectSubListTypeSelected)
  const refetchParams = useSelector(selectRefetchParams)
  const tabRequestParams = useSelector(selectTabRequestParams)
  const channelsType = useSelector(selectChannelsType)
  const lastReFetchTime = useSelector(selectLastReFetchTime)
  const showSkeletonLoader = list.length === 0 && isLoading
  const isChannelsSubList = subListTypeSelected === 'channels'
  const isActive = channelsType === 'channels'
  const isToggled = useSelector(selectIsListDetailsOpen)

  useEffect(() => {
    if (isActive || (isChannelsSubList && saleType)) {
      dispatch(fetchChannels({ ...tabRequestParams, saleType }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, lastReFetchTime, refetchParams, dispatch, isChannelsSubList, saleType])

  return (
    <LoadingOverlay isLoading={showSkeletonLoader} shouldHide={!isActive && !countryId && !saleType}>
      <Box minHeight={showSkeletonLoader ? '6rem' : '0'}>
        {list.map(data => {
          const { channel } = data
          const fetchList = () => {
            if (subListTypeSelected === 'countries') {
              dispatch(fetchCountries({ ...tabRequestParams, channel }))
            }
          }

          return (
            <ChannelSummary
              className={classNames({
                [classes.details]: isToggled,
                [classes.root]: countryId || saleType,
              })}
              channel={channel}
              data={data}
              detailsType={detailsType}
              fetchList={fetchList}
              isLoading={isLoading}
              key={channel}
              label={channel}
              saleType={saleType}
              type="channels"
            >
              {!isChannelsSubList && (
                <ListDetails channel={channel} countryId={countryId} data={data} isLoading={isLoading} type="channels" />
              )}
              {!isToggled && (
                <>
                  {subListTypeSelected === 'saleTypes' && <SaleTypesList channel={channel} />}
                  {subListTypeSelected === 'countries' && !countryId && (
                    <CountriesList channel={channel} detailsType={detailsType} />
                  )}
                  {Boolean(countryId || saleType) && (
                    <GroupDetails
                      channel={channel}
                      countryId={countryId}
                      data={data}
                      isLoading={isLoading}
                      saleType={saleType}
                      type="channels"
                    />
                  )}
                </>
              )}
            </ChannelSummary>
          )
        })}
      </Box>
    </LoadingOverlay>
  )
}

export default memo(ChannelsList)
